<template>
    <v-menu
        v-model="menuOpen"
        offset-y
        left
        :close-on-content-click="false"
        max-width="400"
        min-width="320"
    >
        <template v-slot:activator="{ on, attrs }">
            <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                aria-label="Notificações"
                class="mr-1"
            >
                <v-badge
                    :value="unreadCount > 0"
                    :content="unreadCount > 99 ? '99+' : unreadCount"
                    color="red"
                    overlap
                >
                    <v-icon>mdi-bell-outline</v-icon>
                </v-badge>
            </v-btn>
        </template>

        <v-card>
            <v-card-title class="d-flex justify-space-between align-center py-2 px-4">
                <span class="text-subtitle-1 font-weight-medium">Notificações</span>
                <v-btn
                    v-if="unreadCount > 0"
                    text
                    x-small
                    color="primary"
                    @click="markAllAsRead"
                    :loading="markingAllRead"
                >
                    Marcar todas como lidas
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-list
                v-if="notifications.length > 0"
                dense
                class="py-0 notification-list"
                max-height="400"
                style="overflow-y: auto;"
            >
                <template v-for="notification in notifications">
                <v-list-item
                    :key="notification.id"
                    :class="{ 'unread-notification': !notification.read_at }"
                    @click="handleNotificationClick(notification)"
                    three-line
                >
                    <v-list-item-avatar size="40">
                        <v-icon
                            :color="getNotificationColor(notification.type)"
                            size="24"
                        >
                            {{ getNotificationIcon(notification.type) }}
                        </v-icon>
                    </v-list-item-avatar>

                    <v-list-item-content>
                        <v-list-item-title
                            :class="{ 'font-weight-bold': !notification.read_at }"
                            class="text-body-2"
                        >
                            {{ notification.title }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                            {{ notification.message }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle class="text-caption grey--text">
                            {{ formatDate(notification.created_at) }}
                        </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                        <v-btn
                            v-if="!notification.read_at"
                            icon
                            x-small
                            @click.stop="markAsRead(notification)"
                            title="Marcar como lida"
                        >
                            <v-icon x-small color="primary">mdi-check</v-icon>
                        </v-btn>
                        <v-icon v-else x-small color="grey lighten-1">mdi-check-all</v-icon>
                    </v-list-item-action>
                </v-list-item>
                </template>
            </v-list>

            <v-card-text
                v-else
                class="text-center py-8"
            >
                <v-icon size="48" color="grey lighten-2">mdi-bell-off-outline</v-icon>
                <p class="text-body-2 grey--text mt-2 mb-0">
                    Nenhuma notificação
                </p>
            </v-card-text>

            <v-divider v-if="notifications.length > 0" />
        </v-card>
    </v-menu>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { eventBus } from '../../js/eventBus'

const TEACHER_PANEL_OPEN_PARAMS = {
    '/teacher/panel?open=terms': 'terms',
    '/teacher/panel?open=timesheet': 'timesheet',
    '/teacher/panel?open=workload-confirm': 'workload-confirm',
    '/teacher/panel?open=availability': 'availability',
    '/teacher/panel?open=schedule': 'schedule',
    '/teacher/panel?open=classes': 'classes',
    '/teacher/panel?open=disciplines': 'disciplines',
    '/teacher/panel?open=activities': 'activities',
    '/teacher/panel?open=evaluation': 'evaluation',
}

export default {
    name: 'SysNotificationBell',

    data: () => ({
        menuOpen: false,
        markingAllRead: false,
        pollingInterval: null
    }),

    computed: {
        ...mapState('notification', ['notifications', 'loading']),
        ...mapGetters('notification', ['unreadCount']),
        ...mapState('auth', { authUser: 'user', authToken: 'token' }),
        hasSession() {
            return !!(this.authToken && this.authUser?.mode)
        },
        isTeacherPanelRoute() {
            return this.$route.name === 'teacher/page' || this.$route.name === 'teacher-panel'
        }
    },

    watch: {
        menuOpen(val) {
            if (val) {
                this.fetchNotifications()
            }
        }
    },

    mounted() {
        // Só inicia fetch/polling quando já existe sessão autenticada
        if (this.hasSession) {
            this.fetchNotifications()
            this.startPolling()
        } else {
            // Aguarda sessão ser estabelecida para iniciar
            this.$watch('hasSession', (val) => {
                if (val) {
                    this.fetchNotifications()
                    this.startPolling()
                }
            })
        }
        this.setupEventListeners()
    },

    beforeDestroy() {
        this.stopPolling()
        this.removeEventListeners()
    },

    methods: {
        ...mapActions('notification', [
            'ActionFetchNotifications',
            'ActionMarkAsRead',
            'ActionMarkAllAsRead',
            'ActionAddNotification'
        ]),

        setupEventListeners() {
            eventBus.$on('new-notification', this.handleNewNotification)
            eventBus.$on('refresh-notifications', this.fetchNotifications)
        },

        removeEventListeners() {
            eventBus.$off('new-notification', this.handleNewNotification)
            eventBus.$off('refresh-notifications', this.fetchNotifications)
        },

        handleNewNotification(notification) {
            this.ActionAddNotification({
                ...notification,
                read_at: null,
                created_at: notification.created_at || new Date().toISOString()
            })
        },

        async fetchNotifications() {
            try {
                await this.ActionFetchNotifications()
            } catch {
                // Silenciar erro - API pode não existir ainda
            }
        },

        async markAsRead(notification) {
            try {
                await this.ActionMarkAsRead(notification.id)
            } catch (error) {
                console.error('Erro ao marcar notificação como lida:', error)
            }
        },

        async markAllAsRead() {
            this.markingAllRead = true
            try {
                await this.ActionMarkAllAsRead()
            } catch (error) {
                console.error('Erro ao marcar todas como lidas:', error)
            } finally {
                this.markingAllRead = false
            }
        },

        handleNotificationClick(notification) {
            if (!notification.read_at) {
                this.markAsRead(notification)
            }

            if (notification.action_url) {
                this.menuOpen = false
                
                if (this.isTeacherPanelRoute && this.isTeacherPanelUrl(notification.action_url)) {
                    const openParam = this.extractOpenParam(notification.action_url)
                    if (openParam) {
                        eventBus.$emit('teacher-panel-open-modal', openParam)
                        return
                    }
                }
                
                this.$router.push(notification.action_url).catch(() => {})
            }
        },

        isTeacherPanelUrl(url) {
            if (!url) return false
            return url.startsWith('/teacher/panel') && url.includes('open=')
        },

        extractOpenParam(url) {
            if (!url) return null
            
            if (TEACHER_PANEL_OPEN_PARAMS[url]) {
                return TEACHER_PANEL_OPEN_PARAMS[url]
            }
            
            const match = url.match(/[?&]open=([^&]+)/)
            return match ? match[1] : null
        },

        startPolling() {
            this.pollingInterval = setInterval(() => {
                this.fetchNotifications()
            }, 30000)
        },

        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval)
                this.pollingInterval = null
            }
        },

        getNotificationIcon(type) {
            const icons = {
                info: 'mdi-information',
                warning: 'mdi-alert',
                error: 'mdi-alert-circle',
                success: 'mdi-check-circle',
                message: 'mdi-email',
                task: 'mdi-clipboard-check',
                system: 'mdi-cog',
                default: 'mdi-bell'
            }
            return icons[type] || icons.default
        },

        getNotificationColor(type) {
            const colors = {
                info: 'blue',
                warning: 'orange',
                error: 'red',
                success: 'green',
                message: 'purple',
                task: 'teal',
                system: 'grey',
                default: 'primary'
            }
            return colors[type] || colors.default
        },

        formatDate(dateString) {
            if (!dateString) return ''

            const date = new Date(dateString)
            const now = new Date()
            const diffMs = now - date
            const diffMins = Math.floor(diffMs / 60000)
            const diffHours = Math.floor(diffMs / 3600000)
            const diffDays = Math.floor(diffMs / 86400000)

            if (diffMins < 1) return 'Agora'
            if (diffMins < 60) return `Há ${diffMins} min`
            if (diffHours < 24) return `Há ${diffHours}h`
            if (diffDays < 7) return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`

            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
        }
    }
}
</script>

<style scoped>
.notification-list {
    max-height: 400px;
    overflow-y: auto;
}

.unread-notification {
    background-color: rgba(var(--v-primary-base), 0.04);
}

.unread-notification:hover {
    background-color: rgba(var(--v-primary-base), 0.08);
}
</style>
